import React, { useState, useRef, useCallback } from "react";
import { Upload, Link2, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_SIZE = 10 * 1024 * 1024;

const BannerImageInput = ({ imageUrl, onImageChange, token, title, subtitle }) => {
  const [activeTab, setActiveTab] = useState(imageUrl ? "url" : "upload");
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(`Unsupported format: ${file.type}. Use JPG, PNG, or WEBP.`);
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("File too large. Maximum 10MB.");
      return;
    }
    if (file.size === 0) {
      toast.error("Empty file.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      const url = `${API}/files/${res.data.path}`;
      onImageChange(url);
      toast.success("Banner image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  }, [onImageChange, token]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) handleUpload(e.dataTransfer.files);
  }, [handleUpload]);

  return (
    <div data-testid="banner-image-input">
      <Label className="text-gray-700">Banner Image *</Label>

      {/* Tab Switcher */}
      <div className="flex border-b border-gray-200 mt-1 mb-3">
        <button
          type="button"
          data-testid="banner-tab-upload"
          onClick={() => setActiveTab("upload")}
          className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
            activeTab === "upload"
              ? "border-primary text-primary"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <Upload className="w-3 h-3 inline mr-1 -mt-0.5" />
          Upload
        </button>
        <button
          type="button"
          data-testid="banner-tab-url"
          onClick={() => setActiveTab("url")}
          className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
            activeTab === "url"
              ? "border-primary text-primary"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <Link2 className="w-3 h-3 inline mr-1 -mt-0.5" />
          URL
        </button>
      </div>

      {/* Upload Tab */}
      {activeTab === "upload" && (
        <div
          data-testid="banner-upload-area"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
            dragActive ? "border-primary bg-primary/5" : "border-gray-300 hover:border-gray-400 bg-gray-50"
          } ${uploading ? "opacity-50 pointer-events-none" : ""}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            className="hidden"
            data-testid="banner-file-input"
            onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 text-primary animate-spin" />
              <span className="text-sm text-gray-600">Uploading...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Upload className="w-6 h-6 text-gray-400" />
              <p className="text-xs text-gray-600">
                {dragActive ? "Drop image here" : "Click or drag banner image"}
              </p>
              <p className="text-[10px] text-gray-400">JPG, PNG, WEBP — 16:9 ratio recommended</p>
            </div>
          )}
        </div>
      )}

      {/* URL Tab */}
      {activeTab === "url" && (
        <div data-testid="banner-url-area">
          <Input
            value={imageUrl}
            onChange={(e) => onImageChange(e.target.value)}
            placeholder="https://images.unsplash.com/..."
            className="bg-white border-gray-200"
            data-testid="banner-url-input"
          />
        </div>
      )}

      <p className="text-xs text-gray-500 mt-1">Recommended: 16:9 aspect ratio, minimum 800x450px</p>

      {/* Current Image Indicator */}
      {imageUrl && (
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-green-600 font-medium">Image set</span>
          <button
            type="button"
            onClick={() => onImageChange("")}
            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-0.5"
            data-testid="banner-image-clear"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        </div>
      )}

      {/* Preview (existing banner preview with overlay) */}
      {imageUrl && (
        <div className="relative aspect-[16/9] rounded-lg overflow-hidden bg-gray-100 border mt-2" data-testid="banner-image-preview">
          <img
            src={imageUrl}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          <div className="absolute bottom-4 left-4 text-white">
            <h4 className="font-heading text-xl font-bold uppercase">{title || "Title"}</h4>
            {subtitle && <p className="text-gray-200 text-sm mt-1">{subtitle}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerImageInput;
